"use client";

import React, { useCallback } from "react";
import { SimpleTableErrorBoundary } from "./ErrorBoundaries";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useSimpleTableContextExtraction } from "./components/SimpleTable/hooks/useSimpleTableContextExtraction";
import { useSimpleTableInfiniteScroll } from "./components/SimpleTable/hooks/useSimpleTableInfiniteScroll";
import { SimpleTableHeaderSection } from "./components/SimpleTable/sections/SimpleTableHeaderSection";
import { SimpleTableBodySection } from "./components/SimpleTable/sections/SimpleTableBodySection";
import { SimpleTablePaginationSection } from "./components/SimpleTable/sections/SimpleTablePaginationSection";
import { SimpleTableBulkActionsSection } from "./components/SimpleTable/sections/SimpleTableBulkActionsSection";
import { SimpleTableModalsSection } from "./components/SimpleTable/sections/SimpleTableModalsSection";
import type { BaseEntity } from "./types";

export type { SimpleTableDesktopViewProps } from "./components/SimpleTable/types";

/**
 * SimpleTableDesktopView - Main table component for desktop view
 * Orchestrates all table sections and manages state through extracted hooks
 */
export const SimpleTableDesktopView: React.FC = React.memo(function SimpleTableDesktopViewComponent<
  TData extends BaseEntity,
>() {
  // Extract and organize necessary context values for layout coordination
  const { 
    config, 
    autoScrollEnabled, 
    loadMore, 
    hasNextPage, 
    isFetchingNextPage, 
    refs, 
    hideTableBody 
  } = useSimpleTableContextExtraction<TData>();

  // Handle infinite scroll detection (shares the same DOM node as horizontal scroll sync ref)
  const { tableBodyRef: infiniteScrollRef } = useSimpleTableInfiniteScroll(
    autoScrollEnabled ? loadMore : undefined,
    hasNextPage,
    isFetchingNextPage
  );

  const setTableScrollPortRef = useCallback(
    (node: HTMLDivElement | null) => {
      infiniteScrollRef.current = node;
      const syncRef = refs.tableBodyRef;
      if (syncRef) {
        syncRef.current = node;
      }
    },
    [infiniteScrollRef, refs.tableBodyRef]
  );

  // Get isLoading from context - check if data is being fetched
  const isLoading = false; // TODO: Add loading state to context if needed

  return (
    <SimpleTableErrorBoundary entityName={config.displayName}>
      <div
        // In cards/board mode (hideTableBody) the box holds ONLY the toolbar, so it
        // must be content-height (shrink-0). Leaving it flex-1 makes the empty box
        // grow and shove/clip the sibling card grid — the spacing bug. Normal table
        // mode keeps flex-1 so the scrollable body fills the column.
        className={`bg-background rounded-lg border border-border relative flex flex-col min-h-0 ${
          hideTableBody ? "shrink-0" : "flex-1"
        }`}
      >
        {/* Loading Overlay - Shows when data is loading but keeps table visible */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm rounded-lg flex items-center justify-center z-10 pointer-events-none">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Header Controls Section */}
        <SimpleTableHeaderSection />

        {/* Scrollable Table Body Section - Only render if not hideTableBody */}
        {!hideTableBody && (
          <div
            ref={setTableScrollPortRef}
            className="crm-table-scrollport flex-1 min-h-0 overflow-auto"
          >
            <SimpleTableBodySection />
          </div>
        )}

        {/* Pagination Section - Only render if not hideTableBody */}
        {!hideTableBody && (
          <SimpleTablePaginationSection />
        )}
      </div>

      {/* Bulk Actions Section */}
      <SimpleTableBulkActionsSection />

      {/* Modals Section */}
      <SimpleTableModalsSection />
    </SimpleTableErrorBoundary>
  );
}) as typeof SimpleTableDesktopView;
